'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AtSignIcon, KeySquareIcon, UserPenIcon, ScrollTextIcon, BuildingIcon } from 'lucide-react';
import { Link } from '@/i18n/navigation';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupPassword } from '@/components/ui/input-group';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Combobox } from '@/components/ui/combobox';
import { DatePicker } from '@/components/ui/date-picker';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useMetadataStore } from '@/store';
import { getErrorMessage, getLocalizedRouteFromKeys, setValidationErrors } from '@/lib/utils';
import { EMAIL_REGEX, PASSWORD_REGEX } from '@/constants';
import { GenderEnum } from '@/enums';
import { register } from '@/lib/api';
import { RegisterParamsType } from '@/types/auth';
import { useAppLocale, useMetadata } from '@/hooks';
type PropsType = {
  onSuccess?: () => void;
  className?: string;
};
const RegisterForm = t0 => {
  const $ = _c(267);
  const {
    onSuccess,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t = useTranslations();
  const {
    backendLocale
  } = useAppLocale();
  const {
    routeParams
  } = useMetadata();
  const metadata = useMetadataStore(_temp);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [contractDialog, setContractDialog] = useState(null);
  const locale = backendLocale;
  let t2;
  if ($[0] !== backendLocale || $[1] !== routeParams) {
    t2 = getLocalizedRouteFromKeys("/login", backendLocale, routeParams);
    $[0] = backendLocale;
    $[1] = routeParams;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const loginRoute = t2;
  let t3;
  if ($[3] !== metadata?.timezones) {
    t3 = metadata?.timezones ?? [];
    $[3] = metadata?.timezones;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = t3.map(_temp2);
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const timezoneOptions = t4;
  let t5;
  if ($[7] !== metadata?.currencies) {
    t5 = metadata?.currencies ?? [];
    $[7] = metadata?.currencies;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = t5.map(_temp3);
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const currencyOptions = t6;
  let t7;
  if ($[11] !== t) {
    t7 = t("male");
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = {
      label: t7,
      value: GenderEnum.MALE
    };
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t) {
    t9 = t("female");
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = {
      label: t9,
      value: GenderEnum.FEMALE
    };
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t) {
    t11 = t("diverse");
    $[19] = t;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11) {
    t12 = {
      label: t11,
      value: GenderEnum.DIVERSE
    };
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t10 || $[24] !== t12 || $[25] !== t8) {
    t13 = [t8, t10, t12];
    $[23] = t10;
    $[24] = t12;
    $[25] = t8;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const genderOptions = t13;
  let t14;
  let t15;
  if ($[27] !== t) {
    const formSchema = z.object({
      firstname: z.string().min(1, t("required")),
      lastname: z.string().min(1, t("required")),
      company: z.string().optional(),
      phone: z.string().optional(),
      email: z.string().trim().refine(_temp4, t("invalid_email")),
      gender: z.string().min(1, t("required")),
      birthDate: z.date({
        error: t("required")
      }),
      timezone: z.string().min(1, t("required")),
      currencyCode: z.string().optional(),
      password: z.string().min(6, t("min_value", {
        value: 6
      })).max(32, t("max_value", {
        value: 32
      })).refine(_temp5, t("password_pattern")).refine(_temp6, t("password_pattern")),
      passwordConfirm: z.string().min(1, t("required")),
      termsOfUse: z.boolean().refine(_temp7, t("required")),
      confidentialityAgreement: z.boolean().refine(_temp8, t("required")),
      pdpl: z.boolean().refine(_temp9, t("required"))
    }).superRefine((data, ctx) => {
      if (data.password !== data.passwordConfirm) {
        ctx.addIssue({
          code: "custom",
          message: t("passwords_do_not_match"),
          path: ["passwordConfirm"]
        });
      }
    });
    t15 = useForm;
    t14 = zodResolver(formSchema);
    $[27] = t;
    $[28] = t14;
    $[29] = t15;
  } else {
    t14 = $[28];
    t15 = $[29];
  }
  let t16;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const t17 = metadata?.generalSettings?.defaultCurrencyCode || "";
  let t18;
  if ($[31] !== t17) {
    t18 = {
      firstname: "",
      lastname: "",
      company: "",
      phone: "",
      email: "",
      gender: "",
      birthDate: undefined,
      timezone: t16,
      currencyCode: t17,
      password: "",
      passwordConfirm: "",
      termsOfUse: false,
      confidentialityAgreement: false,
      pdpl: false
    };
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t14 || $[34] !== t18) {
    t19 = {
      resolver: t14,
      defaultValues: t18
    };
    $[33] = t14;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  const form = t15(t19);
  let t20;
  if ($[36] !== form || $[37] !== metadata?.currencies || $[38] !== metadata?.generalSettings?.defaultCurrencyCode) {
    t20 = () => {
      const defaultCode = metadata?.currencies?.find(_temp10)?.code ?? metadata?.generalSettings?.defaultCurrencyCode;
      if (defaultCode && !form.getValues("currencyCode")) {
        form.setValue("currencyCode", defaultCode);
      }
    };
    $[36] = form;
    $[37] = metadata?.currencies;
    $[38] = metadata?.generalSettings?.defaultCurrencyCode;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== form || $[41] !== metadata) {
    t21 = [form, metadata];
    $[40] = form;
    $[41] = metadata;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  useEffect(t20, t21);
  let t22;
  if ($[43] !== onSuccess || $[44] !== t) {
    t22 = () => {
      toast.success(t("register_success"));
      setSuccess(true);
      onSuccess?.();
    };
    $[43] = onSuccess;
    $[44] = t;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== form || $[47] !== t) {
    t23 = error => {
      if (error.response?.status === 422) {
        setValidationErrors(error, form.setError);
      } else {
        toast.error(getErrorMessage(error, t("register_error")));
      }
    };
    $[46] = form;
    $[47] = t;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => setLoading(false);
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t22 || $[51] !== t23) {
    t25 = {
      mutationFn: _temp11,
      onSuccess: t22,
      onError: t23,
      onSettled: t24
    };
    $[50] = t22;
    $[51] = t23;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  const {
    mutate
  } = useMutation(t25);
  let t26;
  if ($[53] !== mutate) {
    t26 = data_1 => {
      setLoading(true);
      const payload = {
        firstname: data_1.firstname,
        lastname: data_1.lastname,
        email: data_1.email,
        gender: data_1.gender,
        birthDate: data_1.birthDate.toISOString().split("T")[0],
        timezone: data_1.timezone,
        password: data_1.password,
        passwordConfirm: data_1.passwordConfirm
      };
      if (data_1.company?.trim()) {
        payload.company = data_1.company.trim();
      }
      if (data_1.phone?.trim()) {
        payload.phone = data_1.phone.trim();
      }
      if (data_1.currencyCode) {
        payload.currencyCode = data_1.currencyCode;
      }
      mutate(payload);
    };
    $[53] = mutate;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  const onSubmit = t26;
  if (success) {
    const t27 = `flex w-full flex-col items-center gap-6 py-12 text-center ${className}`;
    let t28;
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100"><ScrollTextIcon className="h-8 w-8 text-green-600" /></div>;
      $[55] = t28;
    } else {
      t28 = $[55];
    }
    let t29;
    if ($[56] !== t) {
      t29 = t("register_success_title");
      $[56] = t;
      $[57] = t29;
    } else {
      t29 = $[57];
    }
    let t30;
    if ($[58] !== t29) {
      t30 = <h2 className="text-xl font-semibold">{t29}</h2>;
      $[58] = t29;
      $[59] = t30;
    } else {
      t30 = $[59];
    }
    let t31;
    if ($[60] !== t) {
      t31 = t("register_success");
      $[60] = t;
      $[61] = t31;
    } else {
      t31 = $[61];
    }
    let t32;
    if ($[62] !== t31) {
      t32 = <p className="text-muted-foreground text-sm">{t31}</p>;
      $[62] = t31;
      $[63] = t32;
    } else {
      t32 = $[63];
    }
    let t33;
    if ($[64] !== t30 || $[65] !== t32) {
      t33 = <div className="space-y-2">{t30}{t32}</div>;
      $[64] = t30;
      $[65] = t32;
      $[66] = t33;
    } else {
      t33 = $[66];
    }
    let t34;
    if ($[67] !== t) {
      t34 = t("back_to_login");
      $[67] = t;
      $[68] = t34;
    } else {
      t34 = $[68];
    }
    let t35;
    if ($[69] !== t34) {
      t35 = <Button variant="outline">{t34}</Button>;
      $[69] = t34;
      $[70] = t35;
    } else {
      t35 = $[70];
    }
    let t36;
    if ($[71] !== loginRoute || $[72] !== t35) {
      t36 = <Link href={loginRoute}>{t35}</Link>;
      $[71] = loginRoute;
      $[72] = t35;
      $[73] = t36;
    } else {
      t36 = $[73];
    }
    let t37;
    if ($[74] !== t27 || $[75] !== t33 || $[76] !== t36) {
      t37 = <div className={t27}>{t28}{t33}{t36}</div>;
      $[74] = t27;
      $[75] = t33;
      $[76] = t36;
      $[77] = t37;
    } else {
      t37 = $[77];
    }
    return t37;
  }
  const t27 = `flex w-full flex-col space-y-4 select-none ${className}`;
  let t28;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <UserPenIcon className="h-5 w-5" />;
    $[78] = t28;
  } else {
    t28 = $[78];
  }
  let t29;
  if ($[79] !== t) {
    t29 = t("register");
    $[79] = t;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  let t30;
  if ($[81] !== t29) {
    t30 = <h2 className="flex items-center gap-2 text-lg font-semibold uppercase">{t28}{t29}</h2>;
    $[81] = t29;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  let t31;
  if ($[83] !== t) {
    t31 = t("register_description");
    $[83] = t;
    $[84] = t31;
  } else {
    t31 = $[84];
  }
  let t32;
  if ($[85] !== t31) {
    t32 = <p className="text-muted-foreground text-sm">{t31}</p>;
    $[85] = t31;
    $[86] = t32;
  } else {
    t32 = $[86];
  }
  let t33;
  if ($[87] !== t30 || $[88] !== t32) {
    t33 = <div className="flex flex-col gap-1 border-b pb-4">{t30}{t32}</div>;
    $[87] = t30;
    $[88] = t32;
    $[89] = t33;
  } else {
    t33 = $[89];
  }
  let t34;
  if ($[90] !== form || $[91] !== onSubmit) {
    t34 = form.handleSubmit(onSubmit);
    $[90] = form;
    $[91] = onSubmit;
    $[92] = t34;
  } else {
    t34 = $[92];
  }
  let t35;
  if ($[93] !== loading || $[94] !== t) {
    t35 = t36 => {
      const {
        field
      } = t36;
      return <FormItem><FormLabel required={true} htmlFor="register-firstname">{t("firstname")}</FormLabel><FormControl><InputGroup><InputGroupInput id="register-firstname" placeholder={t("firstname")} {...field} disabled={loading} autoFocus={true} /><InputGroupAddon><UserPenIcon /></InputGroupAddon></InputGroup></FormControl><FormMessage /></FormItem>;
    };
    $[93] = loading;
    $[94] = t;
    $[95] = t35;
  } else {
    t35 = $[95];
  }
  let t36;
  if ($[96] !== form.control || $[97] !== t35) {
    t36 = <FormField control={form.control} name="firstname" render={t35} />;
    $[96] = form.control;
    $[97] = t35;
    $[98] = t36;
  } else {
    t36 = $[98];
  }
  let t37;
  if ($[99] !== loading || $[100] !== t) {
    t37 = t38 => {
      const {
        field: field_0
      } = t38;
      return <FormItem><FormLabel required={true} htmlFor="register-lastname">{t("lastname")}</FormLabel><FormControl><Input id="register-lastname" placeholder={t("lastname")} {...field_0} disabled={loading} /></FormControl><FormMessage /></FormItem>;
    };
    $[99] = loading;
    $[100] = t;
    $[101] = t37;
  } else {
    t37 = $[101];
  }
  let t38;
  if ($[102] !== form.control || $[103] !== t37) {
    t38 = <FormField control={form.control} name="lastname" render={t37} />;
    $[102] = form.control;
    $[103] = t37;
    $[104] = t38;
  } else {
    t38 = $[104];
  }
  let t39;
  if ($[105] !== t36 || $[106] !== t38) {
    t39 = <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">{t36}{t38}</div>;
    $[105] = t36;
    $[106] = t38;
    $[107] = t39;
  } else {
    t39 = $[107];
  }
  let t40;
  if ($[108] !== loading || $[109] !== t) {
    t40 = t41 => {
      const {
        field: field_1
      } = t41;
      return <FormItem><FormLabel required={true} htmlFor="register-email">{t("email")}</FormLabel><FormControl><InputGroup><InputGroupInput id="register-email" type="email" placeholder={t("enter_your_email")} {...field_1} disabled={loading} /><InputGroupAddon><AtSignIcon /></InputGroupAddon></InputGroup></FormControl><FormMessage /></FormItem>;
    };
    $[108] = loading;
    $[109] = t;
    $[110] = t40;
  } else {
    t40 = $[110];
  }
  let t41;
  if ($[111] !== form.control || $[112] !== t40) {
    t41 = <FormField control={form.control} name="email" render={t40} />;
    $[111] = form.control;
    $[112] = t40;
    $[113] = t41;
  } else {
    t41 = $[113];
  }
  let t42;
  if ($[114] !== loading || $[115] !== t) {
    t42 = t43 => {
      const {
        field: field_2
      } = t43;
      return <FormItem><FormLabel htmlFor="register-company">{t("company")}</FormLabel><FormControl><InputGroup><InputGroupInput id="register-company" placeholder={t("company")} {...field_2} disabled={loading} /><InputGroupAddon><BuildingIcon /></InputGroupAddon></InputGroup></FormControl><FormMessage /></FormItem>;
    };
    $[114] = loading;
    $[115] = t;
    $[116] = t42;
  } else {
    t42 = $[116];
  }
  let t43;
  if ($[117] !== form.control || $[118] !== t42) {
    t43 = <FormField control={form.control} name="company" render={t42} />;
    $[117] = form.control;
    $[118] = t42;
    $[119] = t43;
  } else {
    t43 = $[119];
  }
  let t44;
  if ($[120] !== loading || $[121] !== t) {
    t44 = t45 => {
      const {
        field: field_3
      } = t45;
      return <FormItem><FormLabel htmlFor="register-phone">{t("phone")}</FormLabel><FormControl><PhoneInput id="register-phone" international={true} value={field_3.value} onChange={field_3.onChange} disabled={loading} /></FormControl><FormMessage /></FormItem>;
    };
    $[120] = loading;
    $[121] = t;
    $[122] = t44;
  } else {
    t44 = $[122];
  }
  let t45;
  if ($[123] !== form.control || $[124] !== t44) {
    t45 = <FormField control={form.control} name="phone" render={t44} />;
    $[123] = form.control;
    $[124] = t44;
    $[125] = t45;
  } else {
    t45 = $[125];
  }
  let t46;
  if ($[126] !== t43 || $[127] !== t45) {
    t46 = <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">{t43}{t45}</div>;
    $[126] = t43;
    $[127] = t45;
    $[128] = t46;
  } else {
    t46 = $[128];
  }
  let t47;
  if ($[129] !== genderOptions || $[130] !== loading || $[131] !== t) {
    t47 = t48 => {
      const {
        field: field_4
      } = t48;
      return <FormItem><FormLabel required={true} htmlFor="register-gender">{t("gender")}</FormLabel><FormControl><Combobox id="register-gender" options={genderOptions} value={field_4.value} onValueChange={val_5 => field_4.onChange(val_5 as string)} disabled={loading} clearable={true} /></FormControl><FormMessage /></FormItem>;
    };
    $[129] = genderOptions;
    $[130] = loading;
    $[131] = t;
    $[132] = t47;
  } else {
    t47 = $[132];
  }
  let t48;
  if ($[133] !== form.control || $[134] !== t47) {
    t48 = <FormField control={form.control} name="gender" render={t47} />;
    $[133] = form.control;
    $[134] = t47;
    $[135] = t48;
  } else {
    t48 = $[135];
  }
  let t49;
  if ($[136] !== backendLocale || $[137] !== form.formState.errors.birthDate || $[138] !== loading || $[139] !== t) {
    t49 = t50 => {
      const {
        field: field_5
      } = t50;
      return <FormItem><FormLabel required={true} htmlFor="register-birthdate">{t("date_of_birth")}</FormLabel><FormControl><DatePicker id="register-birthdate" value={field_5.value} onChange={field_5.onChange} locale={backendLocale} disabled={loading} isInvalid={!!form.formState.errors.birthDate} /></FormControl><FormMessage /></FormItem>;
    };
    $[136] = backendLocale;
    $[137] = form.formState.errors.birthDate;
    $[138] = loading;
    $[139] = t;
    $[140] = t49;
  } else {
    t49 = $[140];
  }
  let t50;
  if ($[141] !== form.control || $[142] !== t49) {
    t50 = <FormField control={form.control} name="birthDate" render={t49} />;
    $[141] = form.control;
    $[142] = t49;
    $[143] = t50;
  } else {
    t50 = $[143];
  }
  let t51;
  if ($[144] !== t48 || $[145] !== t50) {
    t51 = <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">{t48}{t50}</div>;
    $[144] = t48;
    $[145] = t50;
    $[146] = t51;
  } else {
    t51 = $[146];
  }
  let t52;
  if ($[147] !== loading || $[148] !== t || $[149] !== timezoneOptions) {
    t52 = t53 => {
      const {
        field: field_6
      } = t53;
      return <FormItem><FormLabel required={true} htmlFor="register-timezone">{t("timezone")}</FormLabel><FormControl><Combobox id="register-timezone" options={timezoneOptions} value={field_6.value} onValueChange={val_6 => field_6.onChange(val_6 as string)} disabled={loading} clearable={true} /></FormControl><FormMessage /></FormItem>;
    };
    $[147] = loading;
    $[148] = t;
    $[149] = timezoneOptions;
    $[150] = t52;
  } else {
    t52 = $[150];
  }
  let t53;
  if ($[151] !== form.control || $[152] !== t52) {
    t53 = <FormField control={form.control} name="timezone" render={t52} />;
    $[151] = form.control;
    $[152] = t52;
    $[153] = t53;
  } else {
    t53 = $[153];
  }
  let t54;
  if ($[154] !== currencyOptions || $[155] !== loading || $[156] !== t) {
    t54 = t55 => {
      const {
        field: field_7
      } = t55;
      return <FormItem><FormLabel htmlFor="register-currency">{t("currency")}</FormLabel><FormControl><Combobox id="register-currency" options={currencyOptions} value={field_7.value} onValueChange={val_7 => field_7.onChange(val_7 as string)} disabled={loading} clearable={true} /></FormControl><FormMessage /></FormItem>;
    };
    $[154] = currencyOptions;
    $[155] = loading;
    $[156] = t;
    $[157] = t54;
  } else {
    t54 = $[157];
  }
  let t55;
  if ($[158] !== form.control || $[159] !== t54) {
    t55 = <FormField control={form.control} name="currencyCode" render={t54} />;
    $[158] = form.control;
    $[159] = t54;
    $[160] = t55;
  } else {
    t55 = $[160];
  }
  let t56;
  if ($[161] !== t53 || $[162] !== t55) {
    t56 = <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">{t53}{t55}</div>;
    $[161] = t53;
    $[162] = t55;
    $[163] = t56;
  } else {
    t56 = $[163];
  }
  let t57;
  if ($[164] !== loading || $[165] !== t) {
    t57 = t58 => {
      const {
        field: field_8
      } = t58;
      return <FormItem><FormLabel required={true} htmlFor="register-password">{t("password")}</FormLabel><FormControl><InputGroup><InputGroupPassword id="register-password" placeholder={t("password")} {...field_8} disabled={loading} /><InputGroupAddon><KeySquareIcon /></InputGroupAddon></InputGroup></FormControl><FormMessage /></FormItem>;
    };
    $[164] = loading;
    $[165] = t;
    $[166] = t57;
  } else {
    t57 = $[166];
  }
  let t58;
  if ($[167] !== form.control || $[168] !== t57) {
    t58 = <FormField control={form.control} name="password" render={t57} />;
    $[167] = form.control;
    $[168] = t57;
    $[169] = t58;
  } else {
    t58 = $[169];
  }
  let t59;
  if ($[170] !== loading || $[171] !== t) {
    t59 = t60 => {
      const {
        field: field_9
      } = t60;
      return <FormItem><FormLabel required={true} htmlFor="register-password-confirm">{t("confirm_password")}</FormLabel><FormControl><InputGroup><InputGroupPassword id="register-password-confirm" placeholder={t("confirm_password")} {...field_9} disabled={loading} /><InputGroupAddon><KeySquareIcon /></InputGroupAddon></InputGroup></FormControl><FormMessage /></FormItem>;
    };
    $[170] = loading;
    $[171] = t;
    $[172] = t59;
  } else {
    t59 = $[172];
  }
  let t60;
  if ($[173] !== form.control || $[174] !== t59) {
    t60 = <FormField control={form.control} name="passwordConfirm" render={t59} />;
    $[173] = form.control;
    $[174] = t59;
    $[175] = t60;
  } else {
    t60 = $[175];
  }
  let t61;
  if ($[176] !== t58 || $[177] !== t60) {
    t61 = <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">{t58}{t60}</div>;
    $[176] = t58;
    $[177] = t60;
    $[178] = t61;
  } else {
    t61 = $[178];
  }
  let t62;
  if ($[179] !== t) {
    t62 = t("i_have_read_and_accept");
    $[179] = t;
    $[180] = t62;
  } else {
    t62 = $[180];
  }
  let t63;
  if ($[181] !== t62) {
    t63 = <p className="text-muted-foreground text-xs">{t62}</p>;
    $[181] = t62;
    $[182] = t63;
  } else {
    t63 = $[182];
  }
  let t64;
  if ($[183] !== form) {
    t64 = () => {
      form.setValue("termsOfUse", true);
      form.setValue("confidentialityAgreement", true);
      form.setValue("pdpl", true);
    };
    $[183] = form;
    $[184] = t64;
  } else {
    t64 = $[184];
  }
  let t65;
  if ($[185] !== t) {
    t65 = t("accept_all");
    $[185] = t;
    $[186] = t65;
  } else {
    t65 = $[186];
  }
  let t66;
  if ($[187] !== t64 || $[188] !== t65) {
    t66 = <button type="button" onClick={t64} className="shrink-0 text-xs font-medium hover:underline">{t65}</button>;
    $[187] = t64;
    $[188] = t65;
    $[189] = t66;
  } else {
    t66 = $[189];
  }
  let t67;
  if ($[190] !== t63 || $[191] !== t66) {
    t67 = <div className="flex items-center justify-between gap-2">{t63}{t66}</div>;
    $[190] = t63;
    $[191] = t66;
    $[192] = t67;
  } else {
    t67 = $[192];
  }
  let t68;
  if ($[193] !== t) {
    t68 = t("terms_of_use");
    $[193] = t;
    $[194] = t68;
  } else {
    t68 = $[194];
  }
  const t69 = metadata?.contentSettings?.termsOfUseContract?.[locale] ?? "";
  let t70;
  if ($[195] !== t68 || $[196] !== t69) {
    t70 = {
      name: "termsOfUse" as const,
      label: t68,
      content: t69
    };
    $[195] = t68;
    $[196] = t69;
    $[197] = t70;
  } else {
    t70 = $[197];
  }
  let t71;
  if ($[198] !== t) {
    t71 = t("confidentiality_agreement");
    $[198] = t;
    $[199] = t71;
  } else {
    t71 = $[199];
  }
  const t72 = metadata?.contentSettings?.confidentialityAgreementContract?.[locale] ?? "";
  let t73;
  if ($[200] !== t71 || $[201] !== t72) {
    t73 = {
      name: "confidentialityAgreement" as const,
      label: t71,
      content: t72
    };
    $[200] = t71;
    $[201] = t72;
    $[202] = t73;
  } else {
    t73 = $[202];
  }
  let t74;
  if ($[203] !== t) {
    t74 = t("pdpl_contract");
    $[203] = t;
    $[204] = t74;
  } else {
    t74 = $[204];
  }
  const t75 = metadata?.contentSettings?.pdplContract?.[locale] ?? "";
  let t76;
  if ($[205] !== t74 || $[206] !== t75) {
    t76 = {
      name: "pdpl" as const,
      label: t74,
      content: t75
    };
    $[205] = t74;
    $[206] = t75;
    $[207] = t76;
  } else {
    t76 = $[207];
  }
  let t77;
  if ($[208] !== t70 || $[209] !== t73 || $[210] !== t76) {
    t77 = [t70, t73, t76];
    $[208] = t70;
    $[209] = t73;
    $[210] = t76;
    $[211] = t77;
  } else {
    t77 = $[211];
  }
  const t78 = t77 as const;
  let t79;
  if ($[212] !== form.control || $[213] !== loading || $[214] !== t78) {
    t79 = <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">{t78.map(t80 => {
        const {
          name,
          label,
          content
        } = t80;
        return <FormField key={name} control={form.control} name={name} render={t81 => {
          const {
            field: field_10
          } = t81;
          return <FormItem className="flex flex-col"><div className="flex items-center gap-2"><FormControl><Switch id={`register-${name}`} checked={field_10.value} onCheckedChange={field_10.onChange} disabled={loading} /></FormControl><FormLabel htmlFor={`register-${name}`} className="cursor-pointer items-start leading-snug font-normal"><button type="button" onClick={() => setContractDialog({
                  title: label,
                  content
                })} className="cursor-pointer text-left hover:underline">{label}</button></FormLabel></div><FormMessage className="ml-12" /></FormItem>;
        }} />;
      })}</div>;
    $[212] = form.control;
    $[213] = loading;
    $[214] = t78;
    $[215] = t79;
  } else {
    t79 = $[215];
  }
  let t80;
  if ($[216] !== t67 || $[217] !== t79) {
    t80 = <div className="space-y-2 rounded-md border p-3">{t67}{t79}</div>;
    $[216] = t67;
    $[217] = t79;
    $[218] = t80;
  } else {
    t80 = $[218];
  }
  let t81;
  if ($[219] !== t) {
    t81 = t("register");
    $[219] = t;
    $[220] = t81;
  } else {
    t81 = $[220];
  }
  let t82;
  if ($[221] !== loading || $[222] !== t81) {
    t82 = <Button type="submit" className="w-full uppercase" loading={loading} disabled={loading}>{t81}</Button>;
    $[221] = loading;
    $[222] = t81;
    $[223] = t82;
  } else {
    t82 = $[223];
  }
  let t83;
  if ($[224] !== t34 || $[225] !== t39 || $[226] !== t41 || $[227] !== t46 || $[228] !== t51 || $[229] !== t56 || $[230] !== t61 || $[231] !== t80 || $[232] !== t82) {
    t83 = <form onSubmit={t34} className="space-y-4">{t39}{t41}{t46}{t51}{t56}{t61}{t80}{t82}</form>;
    $[224] = t34;
    $[225] = t39;
    $[226] = t41;
    $[227] = t46;
    $[228] = t51;
    $[229] = t56;
    $[230] = t61;
    $[231] = t80;
    $[232] = t82;
    $[233] = t83;
  } else {
    t83 = $[233];
  }
  let t84;
  if ($[234] === Symbol.for("react.memo_cache_sentinel")) {
    t84 = <Separator />;
    $[234] = t84;
  } else {
    t84 = $[234];
  }
  let t85;
  if ($[235] !== t) {
    t85 = t("already_have_account");
    $[235] = t;
    $[236] = t85;
  } else {
    t85 = $[236];
  }
  let t86;
  if ($[237] !== t85) {
    t86 = <span className="text-muted-foreground mr-1">{t85}</span>;
    $[237] = t85;
    $[238] = t86;
  } else {
    t86 = $[238];
  }
  let t87;
  if ($[239] !== t) {
    t87 = t("login");
    $[239] = t;
    $[240] = t87;
  } else {
    t87 = $[240];
  }
  let t88;
  if ($[241] !== loginRoute || $[242] !== t87) {
    t88 = <Link href={loginRoute} className="text-brand font-medium hover:underline">{t87}</Link>;
    $[241] = loginRoute;
    $[242] = t87;
    $[243] = t88;
  } else {
    t88 = $[243];
  }
  let t89;
  if ($[244] !== t86 || $[245] !== t88) {
    t89 = <div className="flex justify-center text-sm">{t86}{t88}</div>;
    $[244] = t86;
    $[245] = t88;
    $[246] = t89;
  } else {
    t89 = $[246];
  }
  let t90;
  if ($[247] !== t27 || $[248] !== t33 || $[249] !== t83 || $[250] !== t89) {
    t90 = <div className={t27}>{t33}{t83}{t84}{t89}</div>;
    $[247] = t27;
    $[248] = t33;
    $[249] = t83;
    $[250] = t89;
    $[251] = t90;
  } else {
    t90 = $[251];
  }
  const t91 = !!contractDialog;
  let t92;
  if ($[252] === Symbol.for("react.memo_cache_sentinel")) {
    t92 = open => {
      if (!open) {
        setContractDialog(null);
      }
    };
    $[252] = t92;
  } else {
    t92 = $[252];
  }
  const t93 = contractDialog?.title;
  let t94;
  if ($[253] !== t93) {
    t94 = <DialogHeader className="border-b px-6 py-4"><DialogTitle>{t93}</DialogTitle></DialogHeader>;
    $[253] = t93;
    $[254] = t94;
  } else {
    t94 = $[254];
  }
  const t95 = contractDialog?.content;
  let t96;
  if ($[255] !== t95) {
    t96 = <ScrollArea className="min-h-0 flex-1" viewportClassName="max-h-[calc(85vh-5rem)] px-6 py-5"><div className="prose prose-sm max-w-none pb-1 text-sm leading-relaxed whitespace-pre-wrap">{t95}</div></ScrollArea>;
    $[255] = t95;
    $[256] = t96;
  } else {
    t96 = $[256];
  }
  let t97;
  if ($[257] !== t94 || $[258] !== t96) {
    t97 = <DialogContent aria-describedby={undefined} className="max-h-[85vh] sm:max-w-2xl">{t94}{t96}</DialogContent>;
    $[257] = t94;
    $[258] = t96;
    $[259] = t97;
  } else {
    t97 = $[259];
  }
  let t98;
  if ($[260] !== t91 || $[261] !== t97) {
    t98 = <Dialog open={t91} onOpenChange={t92}>{t97}</Dialog>;
    $[260] = t91;
    $[261] = t97;
    $[262] = t98;
  } else {
    t98 = $[262];
  }
  let t99;
  if ($[263] !== form || $[264] !== t90 || $[265] !== t98) {
    t99 = <Form {...form}>{t90}{t98}</Form>;
    $[263] = form;
    $[264] = t90;
    $[265] = t98;
    $[266] = t99;
  } else {
    t99 = $[266];
  }
  return t99;
};
export default RegisterForm;
function _temp(state) {
  return state.metadata;
}
function _temp2(tz) {
  return {
    label: `(${tz.offset}) ${tz.id}`,
    value: tz.id,
    searchValue: tz.id
  };
}
function _temp3(c) {
  return {
    label: `${c.code} – ${c.name}`,
    value: c.code,
    searchValue: `${c.code} ${c.name}`
  };
}
function _temp4(val) {
  return EMAIL_REGEX.test(val);
}
function _temp5(val_0) {
  return PASSWORD_REGEX.test(val_0);
}
function _temp6(val_1) {
  return !/\s/.test(val_1);
}
function _temp7(val_2) {
  return val_2;
}
function _temp8(val_3) {
  return val_3;
}
function _temp9(val_4) {
  return val_4;
}
function _temp10(c_0) {
  return c_0.isDefault;
}
async function _temp11(data_0) {
  return register(data_0);
}